import { useMemo, useState } from "react";
import {
  Button,
  DatePicker,
  Flex,
  message,
  Modal,
  PaginationProps,
  Typography,
} from "antd";
import { Dayjs } from "dayjs";
import { DEFAULT_PAGINATION } from "@/constants/table";
import {
  useDeleteExpense,
  useGetExpenses,
  useGetExpensesOptions,
} from "./hooks/useExpense";
import useModal from "@/hooks/useModal";
import { Expense } from "@/types/expense";
import ExpenseEditModal from "./components/ExpenseEditModal";
import { FilterCondition } from "@/components/table/hooks/useFilter";
import Table, { FilterOptions } from "@/components/table";
import { getColumns } from "./utils/columns";
import { SortCondition } from "@/components/table/hooks/useSort";

const ExpenseOverview = () => {
  const [sort, setSort] = useState<SortCondition | undefined>({
    field: "date",
    order: "desc",
  });
  const [filters, setFilters] = useState<FilterCondition[]>([]);
  const [pagination, setPagination] = useState<PaginationProps>();
  const [dateRange, setDateRange] = useState<[Dayjs, Dayjs]>();
  const startDate = dateRange?.[0]?.startOf("day").toISOString();
  const endDate = dateRange?.[1]?.endOf("day").toISOString();

  const {
    modalData: editModalData,
    isModalOpen: isEditModalOpen,
    openModal: openEditModal,
    closeModal: closeEditModal,
  } = useModal<Expense>();

  const { mutate: deleteExpense } = useDeleteExpense();
  const { data, isFetching, refetch } = useGetExpenses({
    params: {
      date_between: dateRange ? `${startDate},${endDate}` : undefined,
      sort: sort ? [{ field: sort.field, order: sort.order }] : undefined,
      filters: filters.length > 0 ? filters : undefined,
      limit: pagination?.pageSize,
      page: pagination?.current,
      offset: ((pagination?.current || 1) - 1) * (pagination?.pageSize || 10),
    },
  });

  // Get filter options
  const { data: options } = useGetExpensesOptions({
    params: {
      only: "expense_type.id,expense_type.name,description.id,description.name,supplier.id,supplier.name",
      date_between: dateRange ? `${startDate},${endDate}` : undefined,
      limit: -1,
    },
  });

  const filterOptions = useMemo(
    () => ({
      expense_type_id: (options || []).reduce((acc, item) => {
        if (
          item.expense_type &&
          !acc.some((option) => option.value === item?.expense_type?.id)
        ) {
          acc.push({
            text: item.expense_type.name,
            value: item.expense_type.id,
          });
        }
        return acc;
      }, [] as FilterOptions[]),
      description_id: (options || []).reduce((acc, item) => {
        if (
          item.description &&
          !acc.some((option) => option.value === item?.description?.id)
        ) {
          acc.push({
            text: item.description.name,
            value: item.description.id,
          });
        }
        return acc;
      }, [] as FilterOptions[]),
      supplier_id: (options || []).reduce((acc, item) => {
        if (
          item.supplier &&
          !acc.some((option) => option.value === item?.supplier?.id)
        ) {
          acc.push({
            text: item.supplier.name,
            value: item.supplier.id,
          });
        }
        return acc;
      }, [] as FilterOptions[]),
    }),
    [options]
  );

  const expenses = data?.data || [];
  const expensesPagination = data?.pagination;

  const handleDelete = (expense: Expense) => {
    Modal.confirm({
      title: "Remove this expense?",
      content: "This action cannot be undone.",
      okText: "Remove Expense",
      okType: "danger",
      cancelText: "Cancel",
      onOk: () => {
        deleteExpense(expense.id, {
          onSuccess: () => {
            message.success("Successfully removed expense");
            refetch();
          },
        });
      },
    });
  };

  const handleClickNewExpense = () => {
    window.location.href = "/expenses/record";
  };

  return (
    <>
      <Typography.Title level={2} style={{ marginBottom: 24 }}>
        Expense Details
      </Typography.Title>

      <Flex vertical={true} gap={16}>
        <Flex vertical={false} justify="space-between" align="center" gap={16}>
          <DatePicker.RangePicker
            value={dateRange}
            onChange={(dates) => setDateRange(dates as [Dayjs, Dayjs])}
          />

          <Button type="primary" onClick={handleClickNewExpense}>
            New Expense
          </Button>
        </Flex>

        <Table
          columns={(props) =>
            getColumns({
              ...props,
              onEdit: openEditModal,
              onDelete: handleDelete,
              filterOptions,
            })
          }
          dataSource={expenses}
          loading={isFetching}
          pagination={{
            defaultPagination: {
              ...DEFAULT_PAGINATION,
              showSizeChanger: true,
            },
            dataPagination: expensesPagination,
            onChange: setPagination,
          }}
          scroll={{
            x: "max-content",
          }}
          initialSort={sort}
          onSort={setSort}
          onFilter={setFilters}
        />
      </Flex>

      <ExpenseEditModal
        isOpen={isEditModalOpen}
        data={editModalData}
        onClose={closeEditModal}
        onRefetch={refetch}
      />
    </>
  );
};

export default ExpenseOverview;
